import { gql, useQuery } from "@apollo/client";
import { Paper, Stack, Typography } from "@mui/material";
import { endOfDay, format, startOfDay } from "date-fns";
import queryString from "query-string";
import React from "react";
import { useLocation } from "react-router-dom";
import { useBranchContext } from "../../../contexts/branchContext";
import useDateFilter from "../../../hooks/useDateFilter";
import useQueryString from "../../../hooks/useQueryString";
import AdminListContainer from "../../../styles/components/AdminListContainer";
import DataTable from "../../common/DataTable";
import DateFilter from "../../common/DateFilter";
import Loading from "../../common/Loading";
import Meta from "../../common/Meta";
import Pagination from "../../common/Pagination";
import Search from "../../common/Search";

export const WASTAGES = gql`
  query Wastages(
    $skip: Int
    $take: Int
    $where: WastageWhereInput
    $orderBy: [WastageOrderByWithRelationInput!]
  ) {
    wastages(skip: $skip, take: $take, where: $where, orderBy: $orderBy) {
      id
      name
      weightInKg
      date
      remarks
      branch {
        id
        name
      }
    }
  }
`;

const WASTAGES_COUNT = gql`
  query WastagesCount($where: WastageWhereInput) {
    wastagesCount(where: $where)
  }
`;

const WASTAGES_TOTAL = gql`
  query WastagesTotal($where: WastageWhereInput) {
    wastagesTotal(where: $where)
  }
`;

const WastagesPage = () => {
  const title = `Wastages`;
  const { branchFilterQueryString } = useBranchContext();

  const paramsObj = queryString.parse(useLocation().search);
  const page = parseInt(paramsObj.page) || 1;
  const perPage = 50;

  const {
    handleChangeStartDate,
    handleChangeEndDate,
    handleClearDateFilters,
    startDate,
    endDate,
    startDateQueryString,
    endDateQueryString,
  } = useDateFilter();
  const {
    handleChangeQueryString: handleChangeSearch,
    handleSubmitQueryString: handleSubmitSearch,
    queryStringValue: searchTerm,
    initialQueryStringValue: searchQueryString,
  } = useQueryString("search");

  const where = {
    ...(startDate &&
      endDate && {
        date: {
          gte: startOfDay(new Date(startDateQueryString)),
          lte: endOfDay(new Date(endDateQueryString)),
        },
      }),
    ...(branchFilterQueryString && {
      branch: {
        id: {
          equals: branchFilterQueryString,
        },
      },
    }),
    ...(searchQueryString && {
      OR: [
        {
          remarks: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
        {
          name: {
            contains: searchQueryString,
            mode: "insensitive",
          },
        },
      ],
    }),
  };
  const { data: { wastages } = {}, loading } = useQuery(WASTAGES, {
    fetchPolicy: "network-only",
    variables: {
      skip: page * perPage - perPage,
      take: perPage,
      where,
      orderBy: [{ date: "desc" }],
    },
  });
  const { data: { wastagesTotal } = {} } = useQuery(WASTAGES_TOTAL, {
    fetchPolicy: "network-only",
    variables: {
      where,
    },
  });

  const columns = [
    {
      header: "Branch",
      accessor: "branch.name",
    },
    {
      header: "Date",
      accessor: "date",
      Cell: (children) => children && format(new Date(children), "dd MMM yyyy"),
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Weight (kg)",
      accessor: "weightInKg",
      Cell: (children) => children && children.toLocaleString(),
    },
    {
      header: "Remarks",
      accessor: "remarks",
    },
  ];

  if (loading) return <Loading />;
  return (
    <>
      <Meta title={`${title} | Meatsumo`} />
      <AdminListContainer>
        <Typography variant="h3" color="primary">
          {title}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Total Weight:{" "}
          {wastagesTotal && `${wastagesTotal.toLocaleString()} kg`}
        </Typography>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: null, md: "flex-end" }}
          spacing={1}
        >
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            handleChangeStartDate={handleChangeStartDate}
            handleChangeEndDate={handleChangeEndDate}
            handleClearDateFilters={handleClearDateFilters}
          />

          <Search
            handleChangeSearch={handleChangeSearch}
            handleSubmitSearch={handleSubmitSearch()}
            searchTerm={searchTerm}
          />
        </Stack>
        <Paper className="dataTableContainer">
          <DataTable data={wastages} columns={columns} />
        </Paper>
        <Pagination
          query={WASTAGES_COUNT}
          variables={{
            where,
          }}
          perPage={perPage}
          page={Number(page)}
        />
      </AdminListContainer>
    </>
  );
};

export default WastagesPage;
